import {trackEvent} from '@lib/infrastructure';
import {
  BlindsValues,
  Controllable,
  ControlValues,
  showTimedFeedback,
  TemperatureValues,
  updateBlinds,
  updateTemperature,
  useAppDispatch,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  getBlindsOptionTitle,
  getBlindsToastMessage,
  getTemperatureOptionTitle,
  getTemperatureToastMessage,
} from '../util';

type ControlOption = {
  title: string;
  isLoading: boolean;
  disabled: boolean;
  isActive: boolean;
  action: () => Promise<void>;
};

type ControlValue = {type: 'blinds'; value: BlindsValues} | {type: 'temperature'; value: TemperatureValues};

export const useControlOptions = (
  {id: controlId, locations, type: controlType, currentValue}: Controllable,
  nodeId: string,
) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const {buildingId} = locations.find((l) => l.nodeId === nodeId) ?? {};
  const [updatingValue, setUpdatingValue] = useState<number | null>(null);
  const isValueCurrentlyUpdating = updatingValue !== null;

  const updateState = useCallback(
    async ({type, value}: ControlValue) => {
      setUpdatingValue(value);

      let result;
      if (type === 'temperature') {
        result = await dispatch(withAsyncThunkErrorHandling(() => updateTemperature({controlId, value})));
      } else {
        result = await dispatch(withAsyncThunkErrorHandling(() => updateBlinds({controlId, value})));
      }

      setUpdatingValue(null);

      if (result.success) {
        const toastMessage =
          type === 'temperature' ? getTemperatureToastMessage(value, t) : getBlindsToastMessage(value, t);
        dispatch(showTimedFeedback({message: toastMessage, type: 'success'}));
        trackEvent(`HereAndNow__UpdateControls`, {
          controlId,
          value,
          buildingId: buildingId ?? '',
          nodeId,
          type,
        });
      }
    },
    [buildingId, controlId, dispatch, nodeId, t],
  );

  const getTemperatureOptions = useCallback(
    (value: TemperatureValues): ControlOption => {
      return {
        isLoading: value === updatingValue,
        disabled: isValueCurrentlyUpdating,
        isActive: value === currentValue,
        title: getTemperatureOptionTitle(value, t),
        action: () => updateState({type: 'temperature', value}),
      };
    },
    [currentValue, isValueCurrentlyUpdating, t, updateState, updatingValue],
  );

  const getBlindsOptions = useCallback(
    (value: BlindsValues): ControlOption => {
      return {
        isLoading: value === updatingValue,
        disabled: isValueCurrentlyUpdating,
        isActive: value === currentValue,
        title: getBlindsOptionTitle(value, t),
        action: () => updateState({type: 'blinds', value}),
      };
    },
    [currentValue, isValueCurrentlyUpdating, t, updateState, updatingValue],
  );

  const options: ControlOption[] = useMemo(() => {
    if (controlType === 'temperature') {
      return ControlValues.temperature.map(getTemperatureOptions);
    } else {
      return ControlValues.blinds.map(getBlindsOptions);
    }
  }, [controlType, getBlindsOptions, getTemperatureOptions]);

  return options;
};
